import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2026-02-19 12:26:35 +0100 (Thu, 19 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160857 $
//  Purpose   : gndo components
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Search, Icon, Button, Header, CardHeader, Card, CardContent } from 'semantic-ui-react';
// ---Framework imports
import { Sel, Actions } from 'uix/registries';
import { XPopup } from 'uix/components';
import { useApi, useState, useActions, useEffect, useSelector, useRef } from 'uix/hooks';
import { XMenu, Toolbar } from 'uix/components';
import log from 'uix/logging';
import { formatDate } from 'uix/utils/date';
// ---Globals
export const GND_TYPES = Object.freeze({
    name: 1,
    subject: 2,
    place: 3
});
export const GND_TYPE_META = {
    [GND_TYPES.name]: {
        entityPrefix: 'name',
        icon: 'user circle'
    },
    [GND_TYPES.subject]: {
        entityPrefix: 'subject',
        icon: 'book'
    },
    [GND_TYPES.place]: {
        entityPrefix: 'place',
        icon: 'globe'
    }
};
export const gndTypeSimplify = (typelist)=>{
    let cases = {
        Person: GND_TYPES.name,
        Family: GND_TYPES.name,
        PlaceOrGeographicName: GND_TYPES.place,
        CorporateBody: GND_TYPES.name,
        SubjectHeading: GND_TYPES.subject,
        Work: GND_TYPES.subject,
        ConferenceOrEvent: GND_TYPES.name
    };
    for (let [key, value] of Object.entries(cases)){
        if (typelist.includes(key)) {
            return value;
        }
    }
    return undefined;
};
const formatPersonDate = (d)=>{
    // for dateOfBirth or dateOfDeath
    if (d.startsWith('-') || !d.includes('-')) {
        // year granularity
        return String(parseInt(d));
    } else {
        return formatDate(d);
    }
};
export const GndLink = ({ gndid, content })=>{
    /* A link to an external website for the GND-ID*/ return /*#__PURE__*/ _jsx("a", {
        style: {
            paddingTop: '0.5rem'
        },
        target: "_gnd",
        href: `https://d-nb.info/gnd/${gndid}`,
        children: content || gndid
    });
};
export const GndPopupInfo = ({ data })=>{
    /* A small info box with basic data for a GND-ID, e.g. to be us ed in a popup. */ let i18n = useSelector(Sel.i18n);
    let lang = useSelector(Sel.language);
    let content = null;
    let header = null;
    if (data?.gndo) {
        let { preferredName, biographicalOrHistoricalInformation, dateOfBirth } = data.gndo;
        let wiki = null;
        Object.values(data?.gndo.sameAs).map((value)=>{
            value.id.includes(`${lang == 'de' || lang == 'en' ? lang : 'en'}.wikipedia`) && (wiki = value.id);
        });
        if (preferredName) {
            header = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Header, {
                        style: {
                            margin: 'auto 0'
                        },
                        as: "h3",
                        children: preferredName
                    }),
                    /*#__PURE__*/ _jsxs(Toolbar, {
                        size: "default",
                        attached: false,
                        style: {
                            marginTop: '0',
                            width: 'inherit'
                        },
                        children: [
                            /*#__PURE__*/ _jsx(XMenu.Item, {
                                size: "small",
                                icon: "dnb",
                                title: "dnb-Link",
                                onClick: (e)=>e.stopPropagation(),
                                href: `${data.gndo.id}`,
                                target: "_blank"
                            }),
                            wiki && /*#__PURE__*/ _jsx(XMenu.Item, {
                                style: {
                                    marginLeft: '0.5rem'
                                },
                                icon: "wikipedia",
                                size: "small",
                                title: "wikipedia-Link",
                                onClick: (e)=>e.stopPropagation(),
                                href: `${wiki}`,
                                target: "_blank"
                            })
                        ]
                    })
                ]
            });
            if (dateOfBirth && dateOfBirth.length) {
                content = /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(CardContent, {
                        children: /*#__PURE__*/ _jsxs("span", {
                            children: [
                                " (",
                                formatPersonDate(dateOfBirth[0]),
                                ")"
                            ]
                        })
                    })
                });
            }
        }
        if (biographicalOrHistoricalInformation) {
            content = /*#__PURE__*/ _jsx(_Fragment, {
                children: /*#__PURE__*/ _jsx(CardContent, {
                    children: /*#__PURE__*/ _jsx("div", {
                        children: biographicalOrHistoricalInformation[0]
                    })
                })
            });
        }
    } else if (!data) {
        header = /*#__PURE__*/ _jsx(CardHeader, {
            children: i18n.get('noEntriesAvailable')
        });
    }
    return /*#__PURE__*/ _jsxs(Card, {
        fluid: true,
        style: {
            maxWidth: '250px'
        },
        children: [
            /*#__PURE__*/ _jsx(CardHeader, {
                style: {
                    padding: '0.3rem 0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    verticalAlign: 'middle'
                },
                children: header
            }),
            content
        ]
    });
};
const GndPopupData = ({ gndid, otid })=>{
    let { data, loading } = useApi('gndo/getLiveInfo', {
        gndid,
        otid
    }, {
        cached: true,
        condition: gndid || otid
    });
    if (loading) return null;
    if (!data) return null;
    return /*#__PURE__*/ _jsx(GndPopupInfo, {
        data: data
    });
};
export const GndPopup = ({ gndid, otid, trigger })=>{
    return /*#__PURE__*/ _jsx(XPopup, {
        fitted: true,
        basic: true,
        wide: true,
        hoverable: true,
        position: "left center",
        on: "hover",
        mouseEnterDelay: 500,
        trigger: trigger,
        children: /*#__PURE__*/ _jsx(GndPopupData, {
            gndid: gndid,
            otid: otid
        })
    });
};
export const GndItemDetails = ({ gndid })=>{
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GndPopupInfo, {
                gndid: gndid
            }),
            /*#__PURE__*/ _jsx(GndLink, {
                gndid: gndid,
                content: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Icon, {
                            name: "linkify"
                        }),
                        gndid
                    ]
                })
            })
        ]
    });
};
export const GndSelect = ({ init, onResultSelect, selected_words })=>{
    let [callApi, errorNotification] = useActions([
        Actions.callApi,
        Actions.errorNotification
    ]);
    let [result_gndid, set_result_gndid] = useState(null);
    // XXX: change this "init" implementation
    useEffect(()=>set_result_gndid(null), [
        init
    ]);
    let content = null;
    if (result_gndid) {
        content = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(GndItemDetails, {
                    gndid: result_gndid
                }),
                /*#__PURE__*/ _jsx(Button, {
                    icon: "check",
                    content: "Übernehmen",
                    title: "Setzt die Metadaten auf diesen Eintrag",
                    onClick: ()=>{
                        // TODO: can result_gndid be changed inbetween? (-> incorrect)
                        callApi('gndo/getLiveInfo', {
                            gndid: result_gndid
                        }, (data, error)=>{
                            if (!error) {
                                let simplified_type = gndTypeSimplify(data.gndo.type);
                                if (simplified_type !== undefined) {
                                    onResultSelect(simplified_type, data.gndid);
                                } else {
                                    errorNotification({
                                        header: 'Fehler beim Speichern.',
                                        message: 'Typ wurde nicht verstanden.'
                                    });
                                }
                            }
                        }, {
                            cached: true
                        });
                    }
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(GndSearch, {
                init: init,
                onResultSelect: (gnid)=>{
                    set_result_gndid(gnid);
                },
                selected_words: selected_words
            }),
            content
        ]
    });
};
export const GndSearch = ({ init, onSearch, onResultSelect, selected_words })=>{
    /* An input widget with autocomplete functionality to select a GND ID.
  Visually displays a text, while the corresponding value will be the gnd id.

  The user can enter both text or an ID for input/search.
  */ let [loading, set_loading] = useState(false);
    let [search_value, _set_search_value] = useState('');
    let default_value = null // initial value
    ;
    let [suggestions, set_suggestions] = useState([]);
    let [callApi] = useActions([
        Actions.callApi
    ]);
    let searchRef = useRef();
    // XXX: review this "init" implementation (do a editCancel->startEdit cycle?, make GndSearch/GndSelect be controlled?)
    useEffect(()=>set_search_value(''), [
        init
    ]);
    let set_search_value = (query)=>{
        if (query.length > 2) {
            update_suggestions(query);
        } else {
            set_suggestions([]);
        }
        _set_search_value(query);
    };
    let update_suggestions = (query)=>{
        // TODO: debounce this?
        set_loading(true);
        callApi('searchGndo', {
            query,
            suggest: true,
            size: 30
        }, ({ result }, error)=>{
            if (!error) {
                set_suggestions(result.map((r)=>({
                        title: r.label,
                        id: r.id.match(/([^\/]+)$/)[1],
                        description: r.category
                    })));
            }
            set_loading(false);
            searchRef.current.open();
        }, {
            cached: true
        });
    };
    return /*#__PURE__*/ _jsx(Search, {
        fluid: true,
        //icon={null}
        loading: loading,
        //defaultValue={default_value}
        minCharacters: 2,
        noResultsMessage: "Keine GND IDs gefunden.",
        onResultSelect: (e, { result })=>{
            onResultSelect(result.id);
        },
        onSearchChange: (e, { value })=>{
            set_search_value(value);
        },
        onClick: (e)=>{
            if (!search_value) {
                set_search_value(selected_words.join(''));
            }
        },
        resultRenderer: (result)=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            lineHeight: '1.5em',
                            maxHeight: '1.5em',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        },
                        title: result.title,
                        children: result.title
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        // TODO: grab color (and font-size) from a style-sheet?
                        style: {
                            fontSize: '0.8em',
                            lineHeight: '1.2em',
                            maxHeight: '1.2em',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            color: 'gray'
                        },
                        title: result.description,
                        children: result.description
                    })
                ]
            }),
        results: suggestions,
        value: search_value,
        ref: searchRef
    });
};
